interface Props {
  page: number;
  total: number;
  limit: number;
  onChange: (page: number) => void;
}

export function Pagination({ page, total, limit, onChange }: Props) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  // Build the list of page buttons (up to 5, with ellipsis)
  const pages: (number | 'ellipsis-start' | 'ellipsis-end')[] = [];
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    // Always show first and last; show up to 3 around current
    const neighbours = new Set<number>();
    neighbours.add(1);
    neighbours.add(totalPages);
    for (let i = Math.max(1, page - 1); i <= Math.min(totalPages, page + 1); i++) {
      neighbours.add(i);
    }
    const sorted = Array.from(neighbours).sort((a, b) => a - b);
    for (let idx = 0; idx < sorted.length; idx++) {
      const p = sorted[idx];
      if (idx > 0 && p - sorted[idx - 1] > 1) {
        pages.push(idx === 1 ? 'ellipsis-start' : 'ellipsis-end');
      }
      pages.push(p);
    }
  }

  return (
    <div className="flex items-center justify-between py-3 px-1">
      <span className="text-sm text-muted">
        {total === 0 ? 'No results' : `Showing ${start}–${end} of ${total}`}
      </span>
      <div className="flex items-center gap-1">
        <button
          className="btn btn-sm btn-secondary"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          Prev
        </button>
        {pages.map((p, _idx) =>
          p === 'ellipsis-start' || p === 'ellipsis-end' ? (
            <span key={p} className="px-1 text-muted text-sm">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p as number)}
              className={
                p === page
                  ? 'btn btn-sm bg-slate-100 font-semibold'
                  : 'btn btn-sm btn-ghost'
              }
            >
              {p}
            </button>
          )
        )}
        <button
          className="btn btn-sm btn-secondary"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
